// Procedural barefoot-footprint geometry. Drawn once to an offscreen canvas
// at component mount (per scale + per side), then stamped onto the main sand
// depth canvas at each stride. Stamping the cached canvas avoids re-drawing
// toe ovals every frame.
//
// Footprint dimensions chosen to match the spec: ~22px tall × 14px wide,
// with the main pad ~60% of total height, 5 toes in a gentle arc above,
// heel below. Right-foot is the mirror of left.

export type FootSide = 'left' | 'right';

const FOOT_WIDTH = 14;
const FOOT_HEIGHT = 22;
// We render to a slightly larger canvas to give the Gaussian falloff room.
const CANVAS_PADDING = 4;
export const FOOT_CANVAS_WIDTH = FOOT_WIDTH + CANVAS_PADDING * 2;
export const FOOT_CANVAS_HEIGHT = FOOT_HEIGHT + CANVAS_PADDING * 2;

// Each geometry blob has its own depth peak so the heel/toes read lighter
// than the main pad — biomechanically accurate (the ball of the foot pushes
// hardest).
type Blob = {
  // Coords relative to the centered footprint origin (pad center).
  x: number;
  y: number;
  // Half-width and half-height of the elliptical falloff region.
  rx: number;
  ry: number;
  // Peak depth value in [0..1].
  depth: number;
};

const LEFT_GEOMETRY: Blob[] = [
  // Main pad (ball of foot). Centered, biggest, hardest stamp.
  { x: 0, y: 0, rx: 4.5, ry: 5.5, depth: 1.0 },
  // Heel — smaller, below the pad.
  { x: -0.5, y: 8.2, rx: 3.2, ry: 3.0, depth: 0.60 },
  // Toes — arc above the pad. Big toe is on the LEFT for a left foot
  // (inside of the gait); pinky on the right (outside).
  { x: -3.5, y: -7.0, rx: 1.9, ry: 1.6, depth: 0.72 }, // big toe
  { x: -1.2, y: -8.2, rx: 1.5, ry: 1.3, depth: 0.65 },
  { x: 1.0, y: -8.4, rx: 1.4, ry: 1.2, depth: 0.62 },
  { x: 2.8, y: -7.8, rx: 1.3, ry: 1.1, depth: 0.58 },
  { x: 4.2, y: -6.6, rx: 1.1, ry: 1.0, depth: 0.55 }, // pinky toe
];

function mirror(blobs: Blob[]): Blob[] {
  return blobs.map((b) => ({ ...b, x: -b.x }));
}

const RIGHT_GEOMETRY: Blob[] = mirror(LEFT_GEOMETRY);

// Render a single footprint to an offscreen canvas as ImageData. Each pixel
// stores the alpha-encoded depth value [0..255]; the renderer that stamps
// this onto the main depth grid reads `data[i+3] / 255` and adds it (capped)
// to the destination depth.
export function makeFootprintImage(
  side: FootSide,
  pixelScale: number,
): ImageData {
  const w = Math.ceil(FOOT_CANVAS_WIDTH * pixelScale);
  const h = Math.ceil(FOOT_CANVAS_HEIGHT * pixelScale);
  const buffer = new Uint8ClampedArray(w * h * 4);
  const data = buffer;
  const blobs = side === 'left' ? LEFT_GEOMETRY : RIGHT_GEOMETRY;

  // Center origin within the canvas
  const ox = w / 2;
  const oy = h / 2;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (x - ox) / pixelScale;
      const dy = (y - oy) / pixelScale;
      let depth = 0;
      for (let i = 0; i < blobs.length; i++) {
        const b = blobs[i];
        const nx = (dx - b.x) / b.rx;
        const ny = (dy - b.y) / b.ry;
        const r2 = nx * nx + ny * ny;
        if (r2 < 1) {
          // Gaussian-ish falloff (smoother than a hard mask)
          const fall = Math.exp(-r2 * 2.3);
          depth = Math.max(depth, b.depth * fall);
        }
      }
      const idx = (y * w + x) * 4;
      // Encode depth in the alpha channel so stampFootprint can read it.
      const a = Math.min(255, Math.floor(depth * 255));
      data[idx] = 0;
      data[idx + 1] = 0;
      data[idx + 2] = 0;
      data[idx + 3] = a;
    }
  }
  return new ImageData(buffer, w, h);
}

// Returns the side of foot to stamp NEXT given the cumulative stride count.
// Stride 1 = right, 2 = left, 3 = right, etc.
export function strideSide(strideIndex: number): FootSide {
  return strideIndex % 2 === 0 ? 'right' : 'left';
}
